import { apiClient } from './config.js';

/**
 * Story-window reconciliation (STORY_WINDOW_RECONCILIATION_PLAN.md §6, M2).
 * The review list holds the automatic decisions whose confidence was low —
 * `review` (window articles on more than one of the issuer's threads) and
 * substance-3 filings with no closure form — until an operator resolves
 * them. Resolving writes `operator_override` on the row and, for
 * `new_article`, enqueues the masthead's article with the window as
 * context and a `continues` link to its latest piece.
 */

export type ReconcileOverride = 'new_article' | 'associate' | 'dismiss';

export interface ReconciliationRow {
  decision_id: string;
  announcement_id: string;
  announcement_title: string;
  company_code: string | null;
  content_product_id: string | null;
  product_name: string | null;
  decision: string;
  reason: string;
  substance: number | null;
  closure_form: string | null;
  thread_ambiguous: boolean;
  window_dates: string[];
  continues_article_id: string | null;
  existing_article_id: string | null;
  operator_override: string | null;
  created_at: string;
}

export interface ReconciliationList {
  items: ReconciliationRow[];
  total: number;
  page: number;
  page_size: number;
}

export async function listReconciliation(params: {
  page?: number;
  page_size?: number;
  include_resolved?: boolean;
} = {}): Promise<ReconciliationList> {
  const r = await apiClient.get('/reconciliation', { params });
  return r.data as ReconciliationList;
}

export async function resolveReconciliation(
  decisionId: string,
  override: ReconcileOverride,
  opts: { force?: boolean } = {},
): Promise<any> {
  const r = await apiClient.post(`/reconciliation/${decisionId}/resolve`, undefined, {
    params: { decision: override, force: opts.force ?? false },
  });
  return r.data;
}

export interface ReconcilePlan {
  announcement_id: string;
  found: boolean;
  override: string | null;
  summary: string;
  window: {
    session_date: string | null;
    window_dates: string[];
    filing: { title: string; substance: number | null; article_class: boolean };
    filings: Array<{ announcement_id: string; title: string; session_date: string | null; substance: number | null }>;
    articles: Array<{ article_id: string; product_name: string; title: string; url: string | null; publish_status: string }>;
    thread_ambiguous: boolean;
  };
  products: Array<{
    product_id: string;
    product_name: string;
    decision: string;
    reason: string;
    continues_article_id: string | null;
    continues_title: string | null;
    continues_url: string | null;
    existing_article: { article_id: string; publish_status: string } | null;
    skip_job: boolean;
    job_id: string | null;
  }>;
  enqueued?: string[];
  skipped?: Array<{ product_id: string; why: string }>;
  dry_run?: boolean;
}

export async function previewReconcile(
  announcementId: string,
  opts: { override?: ReconcileOverride; productIds?: string[] } = {},
): Promise<ReconcilePlan> {
  const r = await apiClient.get(`/announcements/${announcementId}/reconcile`, {
    params: { decision: opts.override, product_id: opts.productIds },
  });
  return r.data as ReconcilePlan;
}

export async function applyReconcile(
  announcementId: string,
  opts: { override?: ReconcileOverride; productIds?: string[]; force?: boolean } = {},
): Promise<ReconcilePlan> {
  const r = await apiClient.post(`/announcements/${announcementId}/reconcile`, undefined, {
    params: { decision: opts.override, product_id: opts.productIds, force: opts.force ?? false },
  });
  return r.data as ReconcilePlan;
}
